"use client";
//* Libraries imports
import { useRef, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

//* App imports
import { AppDispatch } from "@/app/store";

//* Features imports
import { fetchPhaseFormById } from "@/features/phaseForm/phaseFormSlice";
import { createFilledForm } from "@/features/filledForm/filledFormSlice";
import { Status } from "@/features/filledForm/types";
import { PhaseFormFieldType } from "@/features/phaseForm/types";

//* UI Components
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";

//* Config imports
import { API_URL } from "@/config/api";

//* Field components
import { SimplifiedTextField } from "./fields/SimplifiedTextField";
import { SimplifiedStringField } from "./fields/SimplifiedStringField";
import { SimplifiedDateField } from "./fields/SimplifiedDateField";
import { SimplifiedSelectField } from "./fields/SimplifiedSelectField";
import { SimplifiedUploadField } from "./fields/SimplifiedUploadField";
import { SimplifiedNumberField } from "./fields/SimplifiedNumberField";
import { SimplifiedMoneyField } from "./fields/SimplifiedMoneyField";
import { SimplifiedInvoiceField } from "./fields/SimplifiedInvoiceField";
import { TokenDialog } from "./components/TokenDialog";
import { filledFormService } from "@/features/filledForm/filledFormService";
import {
  TokenValidationResponse,
  TokenResendResponse,
  FilledFormData,
} from "@/features/filledForm/types";
import { fetchAllProductsKeys } from "../product";

const formSchema = z.object({
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  state: z.string().min(2, "Deve ter pelo menos 2 caracteres"),
  cnpj: z.string().min(14, "CNPJ deve ter pelo menos 14 caracteres"),
  stateTaxId: z.string().min(14, "Deve ter pelo menos 14 caracteres"),
});

const initialFiles = [
  {
    imageUrl:
      "https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/8cf6f5a3ddaa10fe7e4d581d2b592e366d4c5b31?placeholderIfAbsent=true",
    date: "08/04/25",
    time: "12:20",
  },
  {
    imageUrl:
      "https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/8cf6f5a3ddaa10fe7e4d581d2b592e366d4c5b31?placeholderIfAbsent=true",
    date: "08/04/25",
    time: "12:20",
  },
];

type FirstFormPageProps = {
  clientId?: string | null;
};

export default function FirstFormPage({
  clientId: clientIdProp,
}: FirstFormPageProps) {
  const [files, setFiles] = useState([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { id, processId, productId, clientId } = useParams<{
    id: string;
    processId: string;
    productId?: string;
    clientId?: string;
  }>();
  const resolvedClientId = clientIdProp ?? clientId;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // Estado para armazenar os campos do formulário
  const [formFields, setFormFields] = useState<any[]>([]);
  const [phaseForm, setPhaseForm] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [savedProcessId, setSavedProcessId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const [isClosed, setIsClosed] = useState(false);
  // Adicionar novos estados para controle de acesso
  const [showForm, setShowForm] = useState(true);
  const [forbiddenForm, setForbiddenForm] = useState(false);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [docs, setDocs] = useState([]);

  // Adicionar type guards
  const isApiError = (response: any): response is { error: string } => {
    return "error" in response;
  };

  const isApiResponse = <T,>(response: any): response is { data: T } => {
    return "data" in response;
  };

  // Função para carregar os dados do formulário
  const loadData = useCallback(async () => {
    if (!id || isLoadingRef.current || initialLoadDoneRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const result = await dispatch(fetchPhaseFormById(id)).unwrap();
      let keys = await (await fetch(API_URL + "/products/keys")).json();
      console.log(keys);

      if (
        processId != "new" &&
        result.formFields?.filter(
          (item: any) => item.type == PhaseFormFieldType.BINDING_QUESTION,
        )[0]
      ) {
        let proccess = await (
          await fetch(API_URL + "/processes/" + processId)
        ).json();
        console.log(proccess);
        if (proccess?._id) {
          localStorage.removeItem(`${id}_${processId}_simple`);
          let docs_salved: any = [];
          let products_selecteds: any = [];
          proccess.filledForms.map((item: any) => {
            item.docs?.map((item2: any) => {
              docs_salved.push(item2.formFieldId.replace('docs_',''));
            });
            item.data.map((item2: any) => {
              if (
                item2.field.type == PhaseFormFieldType.BINDING_QUESTION &&
                item2.data
              ) {
                products_selecteds.push(...item2.data);
              }
            });
          });
          if (products_selecteds.length > 0) {
            console.log(products_selecteds);
            console.log(keys);

            let keys_2 = keys.filter((item: any) =>
              products_selecteds.includes(item.value),
            );
            let docs_: any = {};
            console.log(keys_2);
            keys_2.map((item: any) => {
              item.documents.map((item2: any) => {
                if (!docs_salved.includes(item2._id)) {
                  docs_[item2._id] = item2;
                }
              });
            });
            console.log('docs', docs_);
            setDocs(docs_);
          }
        }
      }
      initialLoadDoneRef.current = true;

      setItems(keys);

      setPhaseForm(result);
      setFormFields(result.formFields || []);

      // Verificar se é um formulário fechado
      if (result.type === "CLOSED") {
        setForbiddenForm(true);
        setIsClosed(true);
        setShowForm(false);
      }

      // Carregar dados salvos localmente
      const storageKey = `${id}_${processId}_simple`;
      const savedData = localStorage.getItem(storageKey);

      if (savedData) {
        const data = JSON.parse(savedData);
        if (data.formData) setFormData(data.formData);
        if (data.processId) {
          setSavedProcessId(data.processId);
        }
      } else if (processId === "new") {
        const newProcessId = uuidv4();
        setSavedProcessId(newProcessId);
        saveToLocalStorage("processId", newProcessId);
      }
    } catch (err) {
      console.error("Erro ao carregar formulário:", err);
      setError("Não foi possível carregar o formulário.");
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [dispatch, id, processId]);

  // Efeito para carregar os dados ao montar o componente
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Função para salvar dados no localStorage
  const saveToLocalStorage = (field: string, value: any) => {
    const storageKey = `${id}_${processId}_simple`;
    const savedData = localStorage.getItem(storageKey);
    const data = savedData
      ? JSON.parse(savedData)
      : { formData, processId: savedProcessId };

    data[field] = value;
    localStorage.setItem(storageKey, JSON.stringify(data));
  };

  // Função para lidar com a mudança nos campos
  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev, [fieldId]: value };
      saveToLocalStorage("formData", newData);
      return newData;
    });
  };

  // Função para enviar o formulário
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Formatar os dados para envio
      const formattedData = formFields.map((field) => ({
        field,
        data: formData[field._id],
      }));

      const payload: FilledFormData = {
        phaseForm: id,
        processId: processId === "new" || isClosed ? savedProcessId : processId,
        data: formattedData,
        status: Status.COMPLETED,
        ...(resolvedClientId && { clientId: resolvedClientId }),
      };

      await dispatch(createFilledForm(payload)).unwrap();

      // Limpar localStorage após sucesso
      localStorage.removeItem(`${id}_${processId}_simple`);

      toast.success("Formulário enviado com sucesso!");
      navigate("/simplified-success");
    } catch (error) {
      console.error("Erro ao enviar formulário:", error);
      toast.error("Erro ao enviar formulário. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modificar o handler do token
  const handleTokenSubmit = async (token: string) => {
    try {
      const response = await filledFormService.validateToken(
        processId || "",
        token,
      );
      if (isApiError(response)) {
        toast.error(response.error);
        return;
      }

      if (
        isApiResponse<TokenValidationResponse>(response) &&
        response.data.valid
      ) {
        setTokenDialogOpen(false);
        setShowForm(true);
        setForbiddenForm(false);
        toast.success(response.data.message || "Token validado com sucesso!");
      } else {
        toast.error("Token inválido. Tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao validar token:", error);
      toast.error("Erro ao validar token. Tente novamente.");
    }
  };

  const handleResendToken = async () => {
    try {
      const response = await filledFormService.resendToken(processId || ""); // processId is filledFormId

      // Check if it's an error response
      if (isApiError(response)) {
        toast.error(response.error || "Erro ao reenviar token");
        return;
      }

      // Check if it's a valid API response
      if (isApiResponse<TokenResendResponse>(response)) {
        toast.success(response.data.message || "Token reenviado com sucesso!");
      } else {
        toast.error("Resposta inválida do servidor");
      }
    } catch (error) {
      console.error("Erro ao reenviar token:", error);
      toast.error("Erro ao reenviar token. Tente novamente.");
    }
  };

  // Renderização dos campos dinâmicos
  const renderField = (field: any) => {
    if (!field) return null;

    const commonProps = {
      value: formData[field._id] || "",
      onChange: (value: any) => handleFieldChange(field._id, value),
      label: field.name,
      description: field.description,
      required: field.required,
      videoUrl: field.videoUrl,
    };

    switch (field.type) {
      case PhaseFormFieldType.TEXT:
        return <SimplifiedTextField {...commonProps} />;

      case PhaseFormFieldType.STRING:
        return <SimplifiedStringField {...commonProps} />;

      case PhaseFormFieldType.DATE:
        return <SimplifiedDateField {...commonProps} />;

      case PhaseFormFieldType.SELECT:
        return (
          <SimplifiedSelectField
            {...commonProps}
            options={field.options || []}
            multiline={field.multiline || false}
          />
        );

      case PhaseFormFieldType.NUMBER:
        return <SimplifiedNumberField {...commonProps} />;

      case PhaseFormFieldType.MONEY:
        return <SimplifiedMoneyField {...commonProps} />;

      case PhaseFormFieldType.INVOICE:
        return (
          <SimplifiedInvoiceField
            {...commonProps}
            value={formData[field._id] || []}
            multiline={field.multiline || false}
            processId={processId === "new" ? savedProcessId : processId}
            phaseFormId={id!}
            formFieldId={field._id}
          />
        );

      case PhaseFormFieldType.UPLOAD:
        return (
          <SimplifiedUploadField
            {...commonProps}
            value={formData[field._id] || []}
            multiline={field.multiline || false}
            processId={processId === "new" ? savedProcessId : processId}
            phaseFormId={id!}
            formFieldId={field._id}
          />
        );
      case PhaseFormFieldType.BINDING_QUESTION:
        return (
          <SimplifiedSelectField
            {...commonProps}
            options={items || []}
            multiline={true}
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <main className="flex overflow-hidden flex-wrap gap-10 justify-center items-center px-20 py-10 min-h-screen bg-zinc-50 max-md:px-5">
        <p>Carregando formulário...</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="flex overflow-hidden flex-wrap gap-10 justify-center items-center px-20 py-10 min-h-screen bg-zinc-50 max-md:px-5">
        <p className="text-red-500">{error}</p>
      </main>
    );
  }
  console.log(Object.values(docs));

  return (
    <main className="flex overflow-hidden flex-wrap gap-10 justify-center items-center px-20 py-10 min-h-screen bg-zinc-50 max-md:px-5">
      {forbiddenForm ? (
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold mb-4">Formulário Seguro</h2>
            <p className="text-gray-600 mb-4">
              Este é um formulário protegido. Para acessá-lo, você precisa
              inserir o token que foi enviado por WhatsApp.
            </p>
          </CardContent>
          <CardFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleResendToken}>
              Reenviar Token
            </Button>
            <Button onClick={() => setTokenDialogOpen(true)}>
              Digitar Token
            </Button>
          </CardFooter>
        </Card>
      ) : showForm ? (
        <section className="flex flex-col justify-center h-full min-w-60 w-[722px] max-md:max-w-full">
          <header className="w-40 max-w-full">
            <img
              src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/28589217f7c95b3096b1e549983f5afc4d150520?placeholderIfAbsent=true"
              alt="Company logo"
              className="object-contain w-full aspect-[1.76]"
            />
          </header>

          <div className="flex flex-col justify-center pt-10 w-full max-md:max-w-full" />

          <article className="max-w-full w-[693px]">
            <div className="w-full max-w-[693px] max-md:max-w-full">
              <h1 className="flex flex-col w-full text-6xl tracking-tighter text-[64px] max-md:max-w-full max-md:text-4xl">
                <span className="gap-6 text-gray-700 max-md:max-w-full max-md:text-4xl">
                  {phaseForm?.title || "Formulário"}
                </span>
              </h1>

              <p className="flex-1 shrink gap-2 self-stretch py-4 pl-10 mt-10 w-full text-lg tracking-normal leading-8 text-gray-700 border-l border-solid basis-0 border-l-[color:var(--Claro-Bordas-01,#D8E0F0)] border-slate-200 max-md:pl-5 max-md:max-w-full">
                {phaseForm?.description || "Preencha os campos abaixo."}
              </p>
            </div>
          </article>

          <div className="flex flex-col justify-center pt-8 w-full max-md:max-w-full space-y-6">
            {/* Campos dinâmicos */}
            {Object.values(docs).length == 0 &&
              formFields.map((field) => (
                <div key={field._id} className="w-full">
                  {renderField(field)}
                </div>
              ))}
            {Object.values(docs).length > 0 &&
              Object.values(docs).map((item, id_) => (
                <div key={item._id} className="w-full">
                  <SimplifiedUploadField
                    label={item.name}
                    onChange={(value: any) =>
                      handleFieldChange(item._id, value)
                    }
                    value={formData[item._id] || []}
                    multiline={false}
                    processId={processId}
                    phaseFormId={id!}
                    formFieldId={"docs_" + item._id}
                  />
                </div>
              ))}

            {/* SUBMIT */}
            <div className="flex flex-col justify-center items-end mt-5 w-full max-md:max-w-full">
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex gap-2.5 justify-center items-center max-w-full"
              >
                <span className="flex gap-2.5 items-center justify-center self-stretch p-1 my-auto rounded-lg bg-background h-[26px] w-[26px]">
                  <ArrowUpRight
                    className="text-primary"
                    strokeWidth={2.5}
                    size={14}
                  />
                </span>
                <span className="self-stretch my-auto text-xl font-semibold text-background">
                  {isSubmitting ? "Enviando..." : "Enviar"}
                </span>
              </Button>
            </div>
          </div>
        </section>
      ) : null}

      <aside className="flex overflow-hidden items-center h-full rounded-2xl min-w-60 w-[413px]">
        <img
          src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/caf2e5edb86f8dd7ab636f9d36fad6bb5b30fb10?placeholderIfAbsent=true"
          alt="Decorative side image"
          className="object-contain self-stretch my-auto aspect-[0.57] min-w-60 w-[413px]"
        />
      </aside>

      <TokenDialog
        open={tokenDialogOpen}
        onClose={() => setTokenDialogOpen(false)}
        onSubmit={handleTokenSubmit}
        onResendToken={handleResendToken}
      />
    </main>
  );
}
